import { Bundle, Condition, Resource } from "@medplum/fhirtypes";
import { filterEvidenceResourcesExcludingPatient } from "../../../../../utils/evidence-resource-list-utils";
import type { TableRow } from "../../utils/tables-metadata";

const ICD_10_SYSTEMS = ["http://hl7.org/fhir/sid/icd-10-cm", "http://hl7.org/fhir/sid/icd-10"];

const SUSPECT_TYPE_VALUES = ["suspect", "recapture"] as const;

export type SuspectRowData = TableRow & {
  conditionName: string;
  icd10Code: string;
  suspectType: "suspect" | "recapture";
  hccCode: string | undefined;
  evidenceCount: number;
  firstEvidenceDate: string;
  lastEvidenceDate: string;
  detectionLabel: string;
  detectionLogic: string;
  evidenceResources: Resource[];
};

export function getSuspectRows({ bundles }: { bundles?: Bundle[] }): SuspectRowData[] {
  return (
    bundles?.flatMap(bundle => {
      const entries = bundle.entry?.map(e => e.resource).filter(Boolean) as Resource[] | undefined;
      if (!entries) return [];

      const condition = entries.find(isUnconfirmedCondition) as Condition | undefined;
      if (!condition) return [];

      const evidenceResources = dedupeEvidenceResourcesByFhirIdentity(
        filterEvidenceResourcesExcludingPatient(entries.filter(r => !isUnconfirmedCondition(r)))
      );

      const firstEvidence = condition.evidence?.[0];
      const firstCode = firstEvidence?.code?.[0];
      const manifestationCoding = firstCode?.coding?.[0];

      const hccRaw = getExtensionValue(condition, "hcc-code");
      const hccCode = typeof hccRaw === "string" ? hccRaw : undefined;

      return [
        {
          id: condition.id ?? "",
          conditionName: condition.code?.text ?? condition.code?.coding?.[0]?.display ?? "",
          icd10Code: findIcd10Code(condition),
          suspectType: parseSuspectType(getExtensionValue(condition, "suspect-type")),
          hccCode,
          evidenceCount: parseEvidenceCount(getExtensionValue(condition, "evidence-count")),
          firstEvidenceDate: parseExtensionString(
            getExtensionValue(condition, "first-evidence-date")
          ),
          lastEvidenceDate: parseExtensionString(
            getExtensionValue(condition, "last-evidence-date")
          ),
          detectionLabel: manifestationCoding?.display ?? "",
          detectionLogic: firstCode?.text ?? "",
          evidenceResources,
        },
      ];
    }) ?? []
  );
}

function getExtensionValue(condition: Condition, url: string): string | number | undefined {
  const ext = condition.extension?.find(e => e.url === url);
  if (!ext) return undefined;
  return ext.valueString ?? ext.valueInteger ?? ext.valueDateTime ?? undefined;
}

function parseSuspectType(raw: string | number | undefined): "suspect" | "recapture" {
  if (typeof raw !== "string") {
    return "suspect";
  }
  if ((SUSPECT_TYPE_VALUES as readonly string[]).includes(raw)) {
    return raw as "suspect" | "recapture";
  }
  return "suspect";
}

function parseEvidenceCount(raw: string | number | undefined): number {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
}

function parseExtensionString(raw: string | number | undefined): string {
  return typeof raw === "string" ? raw : "";
}

function isUnconfirmedCondition(resource: Resource): resource is Condition {
  if (resource.resourceType !== "Condition") return false;
  const codings = resource.verificationStatus?.coding;
  if (!codings || codings.length === 0) {
    return false;
  }
  return codings.some(c => c?.code === "unconfirmed");
}

function findIcd10Code(condition: Condition): string {
  const coding = condition.code?.coding?.find(c =>
    ICD_10_SYSTEMS.some(system => c.system === system)
  );
  return coding?.code ?? "";
}

/**
 * Search bundles often repeat the same resource in `entry` (e.g. `_include` / resolver output).
 * Dedupe by FHIR logical identity so the side panel does not list duplicates.
 */
function dedupeEvidenceResourcesByFhirIdentity(resources: Resource[]): Resource[] {
  const seen = new Set<string>();
  const result: Resource[] = [];
  for (const r of resources) {
    const id = r.id != null && String(r.id).length > 0 ? String(r.id) : undefined;
    if (id != null) {
      const key = `${r.resourceType}/${id}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
    }
    result.push(r);
  }
  return result;
}
